import React, { useState, useEffect } from "react";
import axios from "axios";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeft from "@mui/icons-material/KeyboardDoubleArrowLeft";
import ChevronRight from "@mui/icons-material/KeyboardDoubleArrowRight";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import { useBranch } from '../../layouts/BranchContext';
import {
  Box,
  Button,
  Container,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  CircularProgress,
  Chip,
} from "@mui/material";

import {
  Close as CloseIcon,
  Add as AddIcon,
} from "@mui/icons-material";

import MuiAlert from "@mui/material/Alert";

const Alert = React.forwardRef(function Alert(props, ref) {
  return (
    <MuiAlert
      elevation={6}
      ref={ref}
      variant="filled"
      {...props}
    />
  );
});

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://wims.revisetoexcel.com";

const CustomTableScrollbar = styled("div")(({ theme }) => ({
  overflow: "hidden",
  maxHeight: 500,

  "&::-webkit-scrollbar": {
    display: "none",
  },

  scrollbarWidth: "none",

  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.2)"
        : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },

  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.3)"
        : "rgba(0,0,0,0.3)",
  },
}));

const EmployeeAttendance = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  /* =========================================================
     USER DATA
  ========================================================= */

  const getUserData = () => {
    try {
      return JSON.parse(
        sessionStorage.getItem("user") || "{}"
      );
    } catch {
      return {};
    }
  };

  const initialUser = getUserData();

  /* =========================================================
     ORGANIZATION
  ========================================================= */

  const [organizationId] = useState(
    () =>
      initialUser?.organization_id ||
      initialUser?.org_id ||
      initialUser?.organization ||
      ""
  );

 
  /* =========================================================
     FILTERS
  ========================================================= */

  const { selectedBranchId, branchList, isMainBranchAdmin } = useBranch();

  const [selectedDepartment, setSelectedDepartment] =
    useState("");

  const [selectedDesignation, setSelectedDesignation] =
    useState("");

  const [searchTerm, setSearchTerm] =
    useState("");

  const [financialYear, setFinancialYear] =
    useState("2025-2026");

  /* =========================================================
     EMPLOYEE DATA
  ========================================================= */

  const [employees, setEmployees] =
    useState([]);

  const [filteredData, setFilteredData] =
    useState([]);

  const [departments, setDepartments] =
    useState([]);

  const [designations, setDesignations] =
    useState([]);

  const [deptMap, setDeptMap] =
    useState({});

  const [desigMap, setDesigMap] =
    useState({});

  /* =========================================================
     PAGINATION
  ========================================================= */

  const rowsPerPageOptions = [5, 10, 25, 50];

  const [page, setPage] =
    useState(1);

  const [rowsPerPage, setRowsPerPage] =
    useState(5);

  /* =========================================================
     LOADING / ERROR
  ========================================================= */

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState(null);

  /* =========================================================
     ATTENDANCE MODAL
  ========================================================= */

  const [openAttendanceModal, setOpenAttendanceModal] =
    useState(false);

  const [selectedEmployee, setSelectedEmployee] =
    useState(null);

  const [employeeAttendanceData, setEmployeeAttendanceData] =
    useState({});

  const [attendanceHistory, setAttendanceHistory] =
    useState([]);

  const [filteredAttendanceHistory, setFilteredAttendanceHistory] =
    useState([]);

  const [attendanceSummary, setAttendanceSummary] =
    useState(null);

  const [fromDate, setFromDate] =
    useState("");

  const [toDate, setToDate] =
    useState("");

  /* =========================================================
     EDIT ATTENDANCE
  ========================================================= */

  const [openEditDialog, setOpenEditDialog] =
    useState(false);

  const [editingAttendance, setEditingAttendance] =
    useState(null);

  const [editStatus, setEditStatus] =
    useState("");

  const [editTimeIn, setEditTimeIn] =
    useState("");

  const [editTimeOut, setEditTimeOut] =
    useState("");

  const [editRemark, setEditRemark] =
    useState("");

  const [updateLoading, setUpdateLoading] =
    useState(false);

  /* =========================================================
     DELETE ATTENDANCE
  ========================================================= */

  const [openDeleteDialog, setOpenDeleteDialog] =
    useState(false);

  const [deleteAttendanceId, setDeleteAttendanceId] =
    useState(null);

  const [deleteLoading, setDeleteLoading] =
    useState(false);

  /* =========================================================
     USER GUIDE
  ========================================================= */

  const [showUserGuideModal, setShowUserGuideModal] =
    useState(false);

  /* =========================================================
     SNACKBAR
  ========================================================= */

  const [openSnackbar, setOpenSnackbar] =
    useState(false);

  const [snackbarMessage, setSnackbarMessage] =
    useState("");

  const [snackbarSeverity, setSnackbarSeverity] =
    useState("success");



  /* =========================================================
     FETCH EMPLOYEES
  ========================================================= */

  useEffect(() => {
  const fetchEmployees = async () => {
    setLoading(true);
    setError(null);

    try {
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        setEmployees([]);
        setFilteredData([]);
        return;
      }

      const userData = getUserData();
      const params = {
        organization_id: organizationId,
        branch_id: selectedBranchId || userData?.branch_id || "",
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      console.log("Employee API Params:", params);

      const response = await axios.get(
        `${API_BASE_URL}/api/staff/employees/filter/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params,
        }
      );

      const employeesData = response.data?.results || [];
      console.log("Employee API Response:", employeesData);
      setEmployees(Array.isArray(employeesData) ? employeesData : []);
    } catch (err) {
      console.error("Failed to fetch employee data:", err);
      setEmployees([]);
      setFilteredData([]);
      setError("Failed to fetch employee data");
    } finally {
      setLoading(false);
    }
  };

  fetchEmployees();
}, [selectedBranchId, searchTerm, organizationId]);

  /* =========================================================
     DEPARTMENT / DESIGNATION FILTER
  ========================================================= */

  useEffect(() => {
    const filtered =
      employees.filter((employee) => {
        const deptId =
          employee.department_id ??
          employee.dept_id ??
          employee.department ??
          employee.dept ??
          "";

        const desigId =
          employee.designation_id ??
          employee.desig_id ??
          employee.designation ??
          employee.desig ??
          "";

        const departmentMatches =
          !selectedDepartment ||
          String(deptId) ===
            String(selectedDepartment);

        const designationMatches =
          !selectedDesignation ||
          String(desigId) ===
            String(selectedDesignation);

        return (
          departmentMatches &&
          designationMatches
        );
      });

    setFilteredData(filtered);
    setPage(1);
  }, [
    employees,
    selectedDepartment,
    selectedDesignation,
  ]);

  /* =========================================================
     FETCH MASTER DATA
  ========================================================= */

  useEffect(() => {
    const fetchMasterData = async () => {
      const accessToken =
        localStorage.getItem("accessToken");

      if (!accessToken) return;

      try {
        /* -----------------------------
           DEPARTMENTS
        ----------------------------- */

        const deptRes =
          await axios.get(
            `${API_BASE_URL}/api/master/departments/`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

        const depts =
          Array.isArray(deptRes.data)
            ? deptRes.data
            : [];

        setDepartments(depts);

        const deptLookup = {};

        depts.forEach((d) => {
          if (d.id != null && d.name) {
            deptLookup[d.id] = d.name;
          }
        });

        setDeptMap(deptLookup);

        /* -----------------------------
           DESIGNATIONS
        ----------------------------- */

        const desigRes =
          await axios.get(
            `${API_BASE_URL}/api/master/designations/`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

        const desigs =
          Array.isArray(desigRes.data)
            ? desigRes.data
            : [];

        setDesignations(desigs);

        const desigLookup = {};

        desigs.forEach((d) => {
          if (d.id != null && d.name) {
            desigLookup[d.id] = d.name;
          }
        });

        setDesigMap(desigLookup);
      } catch (err) {
        console.error(
          "Failed to load master data:",
          err
        );

        setSnackbarMessage(
          "Could not load departments/designations"
        );

        setSnackbarSeverity("warning");
        setOpenSnackbar(true);
      }
    };

    fetchMasterData();
  }, []);

  /* =========================================================
     DESIGNATION FILTER
  ========================================================= */

  const filteredDesignations =
    selectedDepartment
      ? designations.filter(
          (d) =>
            String(
              d.department_id ??
                d.dept_id ??
                d.department ??
                ""
            ) ===
            String(selectedDepartment)
        )
      : designations;

  /* =========================================================
     PAGINATION
  ========================================================= */

  const totalPages =
    Math.ceil(
      filteredData.length /
        rowsPerPage
    );

  const paginatedData =
    filteredData.slice(
      (page - 1) * rowsPerPage,
      page * rowsPerPage
    );

  const handleChangePage = (newPage) => {
    if (
      newPage < 1 ||
      newPage > totalPages
    ) {
      return;
    }

    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(
      parseInt(e.target.value, 10)
    );

    setPage(1);
  };

  /* =========================================================
     MONTH DATA
  ========================================================= */

  const monthOrder = [
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
    "January",
    "February",
    "March",
  ];

  const getDaysInMonth = (month) => {
    const daysMap = {
      January: 31,
      February: 28,
      March: 31,
      April: 30,
      May: 31,
      June: 30,
      July: 31,
      August: 31,
      September: 30,
      October: 31,
      November: 30,
      December: 31,
    };

    return daysMap[month] || 31;
  };

  /* =========================================================
     FETCH EMPLOYEE ATTENDANCE
  ========================================================= */

  const fetchEmployeeAttendance =
    async (empId) => {
      try {
        const accessToken =
          localStorage.getItem(
            "accessToken"
          );

        if (!accessToken) {
          throw new Error(
            "No access token found"
          );
        }

        if (!empId) {
          throw new Error(
            "Employee ID is missing"
          );
        }

        const response =
          await axios.get(
            `${API_BASE_URL}/api/staff/attendance/employee/${empId}/`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

        const data = response.data;

        console.log(
          "Employee Attendance API Response:",
          data
        );

        /* -----------------------------
           SUMMARY
        ----------------------------- */

        setAttendanceSummary(
          data?.summary || null
        );

        /* -----------------------------
           HISTORY
        ----------------------------- */

        const records =
          Array.isArray(data?.history)
            ? data.history
            : [];

        setFromDate("");
        setToDate("");

        setAttendanceHistory(
          records
        );

        setFilteredAttendanceHistory(
          records
        );

        /* -----------------------------
           MONTHLY DATA
        ----------------------------- */

        const attendanceByMonth = {};

        const monthNames = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];

        monthNames.forEach(
          (month) => {
            attendanceByMonth[month] =
              new Array(31).fill(null);
          }
        );

        records.forEach(
          (record) => {
            if (!record?.date) return;

            const date =
              new Date(record.date);

            if (
              isNaN(date.getTime())
            ) {
              return;
            }

            const monthIndex =
              date.getMonth();

            const day =
              date.getDate();

            const monthName =
              monthNames[
                monthIndex
              ];

            if (
              !monthName ||
              day < 1 ||
              day > 31
            ) {
              return;
            }

            let status = "-";

            if (
              date.getDay() === 0
            ) {
              status = "Sunday";
            } else if (
              record.status ===
              "present"
            ) {
              status = "P";
            } else if (
              record.status ===
              "absent"
            ) {
              status = "A";
            } else if (
              record.status ===
              "half_day"
            ) {
              status = "H";
            } else if (
              record.status ===
              "on_leave"
            ) {
              status = "L";
            }

            // Keep the complete record with the monthly
            // status so hover can show the real Time In / Time Out.
            attendanceByMonth[
              monthName
            ][day - 1] = {
              status,
              record,
            };
          }
        );

        setEmployeeAttendanceData(
          attendanceByMonth
        );
      } catch (err) {
        console.error(
          "Failed to fetch attendance:",
          err
        );

        console.error(
          "Attendance API error response:",
          err.response?.data
        );

        setSnackbarMessage(
          err.response?.data?.detail ||
            err.response?.data?.message ||
            "Failed to load attendance data"
        );

        setSnackbarSeverity(
          "error"
        );

        setOpenSnackbar(true);

        setEmployeeAttendanceData(
          {}
        );
      }
    };

  /* =========================================================
     DATE FILTER
  ========================================================= */

  const applyDateFilter = () => {
    let filtered = [
      ...attendanceHistory,
    ];

    if (fromDate) {
      filtered = filtered.filter(
        (record) =>
          record.date >= fromDate
      );
    }

    if (toDate) {
      filtered = filtered.filter(
        (record) =>
          record.date <= toDate
      );
    }

    setFilteredAttendanceHistory(
      filtered
    );
  };

  const resetDateFilter = () => {
    setFromDate("");
    setToDate("");

    setFilteredAttendanceHistory(
      attendanceHistory
    );
  };

  /* =========================================================
     EDIT ATTENDANCE
  ========================================================= */

  const handleEditAttendance = (
    row
  ) => {
    setEditingAttendance(row);

    setEditStatus(
      row.status || ""
    );

    setEditTimeIn(
      row.time_in
        ? row.time_in.substring(
            0,
            5
          )
        : ""
    );

    setEditTimeOut(
      row.time_out
        ? row.time_out.substring(
            0,
            5
          )
        : ""
    );

    setEditRemark(
      row.remark || ""
    );

    setOpenEditDialog(true);
  };

  /* =========================================================
     UPDATE ATTENDANCE
  ========================================================= */

  const handleUpdateAttendance =
    async () => {
      if (!editingAttendance) {
        return;
      }

      if (
        editStatus === "present" &&
        !editTimeIn
      ) {
        setSnackbarMessage(
          "Time In is required."
        );

        setSnackbarSeverity(
          "warning"
        );

        setOpenSnackbar(true);

        return;
      }

      setUpdateLoading(true);

      try {
        const accessToken =
          localStorage.getItem(
            "accessToken"
          );

        const payload = {
          employee:
            editingAttendance.employee,
          date:
            editingAttendance.date,
          status: editStatus,
          time_in:
            editTimeIn || null,
          time_out:
            editTimeOut || null,
          remark:
            editRemark || null,
        };

        console.log(
          "Update Payload:",
          payload
        );

        const response =
          await axios.put(
            `${API_BASE_URL}/api/staff/attendance/${editingAttendance.id}/`,
            payload,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

        console.log(
          "Attendance Updated:",
          response.data
        );

        setSnackbarMessage(
          "Attendance updated successfully"
        );

        setSnackbarSeverity(
          "success"
        );

        setOpenSnackbar(true);

        setOpenEditDialog(false);

        await fetchEmployeeAttendance(
          editingAttendance.employee
        );
      } catch (err) {
        console.error(
          "Update Error:",
          err.response?.data ||
            err
        );

        setSnackbarMessage(
          err.response?.data
            ?.detail ||
            err.response?.data
              ?.message ||
            "Failed to update attendance"
        );

        setSnackbarSeverity(
          "error"
        );

        setOpenSnackbar(true);
      } finally {
        setUpdateLoading(false);
      }
    };

  /* =========================================================
     DELETE CLICK
  ========================================================= */

  const handleDeleteClick = (
    row
  ) => {
    setDeleteAttendanceId(
      row.id
    );

    setOpenDeleteDialog(true);
  };

  /* =========================================================
     DELETE ATTENDANCE
  ========================================================= */

  const handleDeleteAttendance =
    async () => {
      if (!deleteAttendanceId) {
        return;
      }

      setDeleteLoading(true);

      try {
        const accessToken =
          localStorage.getItem(
            "accessToken"
          );

        const response =
          await axios.delete(
            `${API_BASE_URL}/api/staff/attendance/${deleteAttendanceId}/`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

        console.log(
          "Delete Response:",
          response.data
        );

        setSnackbarMessage(
          response.data?.message ||
            "Attendance deleted successfully"
        );

        setSnackbarSeverity(
          "success"
        );

        setOpenSnackbar(true);

        setOpenDeleteDialog(
          false
        );

        /*
         * FIX:
         * Previously this was:
         *
         * fetchEmployeeAttendance(
         *   editingAttendance.employee
         * );
         *
         * editingAttendance can be null because
         * delete and edit are separate actions.
         */

        const employeeId =
          selectedEmployee?.employee ||
          selectedEmployee?.employee_id ||
          selectedEmployee?.id ||
          selectedEmployee?.pk;

        if (employeeId) {
          await fetchEmployeeAttendance(
            employeeId
          );
        }

        setDeleteAttendanceId(
          null
        );
      } catch (err) {
        console.error(
          "Delete Error:",
          err.response?.data ||
            err
        );

        setSnackbarMessage(
          err.response?.data
            ?.detail ||
            err.response?.data
              ?.message ||
            "Failed to delete attendance"
        );

        setSnackbarSeverity(
          "error"
        );

        setOpenSnackbar(true);
      } finally {
        setDeleteLoading(false);
      }
    };

  /* =========================================================
     RETURN
  ========================================================= */

  return (
    <Container maxWidth="xl">

      {/* =====================================================
          TOP FILTER SECTION
      ===================================================== */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          p: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >

        {/* SEARCH */}

        <TextField
          variant="outlined"
          placeholder="Search by code or name"
          value={searchTerm}
          onChange={(e) =>
            setSearchTerm(
              e.target.value
            )
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{
                    color:
                      theme.palette
                        .primary.main,
                    marginLeft: 1.5,
                    fontSize: 24,
                  }}
                />
              </InputAdornment>
            ),

            endAdornment:
              searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setSearchTerm("")
                    }
                    sx={{
                      color: "grey",
                      marginRight: 1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),

            sx: {
              borderRadius: "30px",

              backgroundColor:
                theme.palette.mode ===
                "dark"
                  ? "rgba(255, 255, 255, 0.15)"
                  : "rgba(255, 255, 255, 1)",

              boxShadow:
                theme.palette.mode ===
                "dark"
                  ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                  : "0 2px 8px rgba(0, 0, 0, 0.1)",

              "& .MuiOutlinedInput-notchedOutline":
                {
                  border:
                    theme.palette.mode ===
                    "dark"
                      ? "1px solid rgba(255,255,255,0.2)"
                      : "1px solid rgba(0,0,0,0.1)",
                },

              "&:hover": {
                backgroundColor:
                  theme.palette.mode ===
                  "dark"
                    ? "rgba(255, 255, 255, 0.2)"
                    : "rgba(240, 240, 240, 1)",
              },

              "&.Mui-focused": {
                backgroundColor:
                  theme.palette.mode ===
                  "dark"
                    ? "rgba(255, 255, 255, 0.25)"
                    : "rgba(255, 255, 255, 1)",

                boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,

                "& .MuiOutlinedInput-notchedOutline":
                  {
                    border: `2px solid ${theme.palette.primary.main}`,
                  },
              },

              paddingLeft: 1.5,
              height: "50px",
              transition:
                "all 0.3s ease",
            },
          }}
          sx={{
            width: 350,
            maxWidth: "100%",

            "& .MuiInputBase-input":
              {
                padding:
                  "10px 14px",
                color:
                  theme.palette.text
                    .primary,
                fontWeight: 500,
              },
          }}
        />

        {/* FINANCIAL YEAR */}

        <FormControl
          sx={{
            width: 200,
            mx: 1,
          }}
        >
          <InputLabel
            sx={{
              color:
                theme.palette.text
                  .primary,

              "&.Mui-focused": {
                color:
                  theme.palette
                    .primary.main,
              },
            }}
          >
            Financial Year
          </InputLabel>

          <Select
            value={financialYear}
            label="Financial Year"
            onChange={(e) =>
              setFinancialYear(
                e.target.value
              )
            }
            sx={{
              color:
                theme.palette.text
                  .primary,

              backgroundColor:
                theme.palette.mode ===
                "dark"
                  ? "rgba(255, 255, 255, 0.15)"
                  : "#fff",

              borderRadius:
                "10px",

              "& fieldset": {
                borderRadius:
                  "10px",
              },
            }}
          >
            <MenuItem value="2023-2024">
              2023-2024
            </MenuItem>

            <MenuItem value="2024-2025">
              2024-2025
            </MenuItem>

            <MenuItem value="2025-2026">
              2025-2026
            </MenuItem>
          </Select>
        </FormControl>

        {/* DEPARTMENT */}

        <FormControl
          sx={{
            width: 200,
            mx: 1,
          }}
        >
          <InputLabel
            sx={{
              color:
                theme.palette.text
                  .primary,

              "&.Mui-focused": {
                color:
                  theme.palette
                    .primary.main,
              },
            }}
          >
            Department
          </InputLabel>

          <Select
            value={selectedDepartment}
            label="Department"
            onChange={(e) => {
              setSelectedDepartment(
                e.target.value
              );

              setSelectedDesignation(
                ""
              );
            }}
            sx={{
              color:
                theme.palette.text
                  .primary,

              backgroundColor:
                theme.palette.mode ===
                "dark"
                  ? "rgba(255, 255, 255, 0.15)"
                  : "#fff",

              borderRadius:
                "10px",

              "& fieldset": {
                borderRadius:
                  "10px",
              },
            }}
          >
            <MenuItem value="">
              All Departments
            </MenuItem>

            {departments.map(
              (dept) => (
                <MenuItem
                  key={dept.id}
                  value={dept.id}
                >
                  {dept.name}
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>

        {/* DESIGNATION */}

        <FormControl
          sx={{
            width: 200,
            mx: 1,
          }}
        >
          <InputLabel
            sx={{
              color:
                theme.palette.text
                  .primary,

              "&.Mui-focused": {
                color:
                  theme.palette
                    .primary.main,
              },
            }}
          >
            Designation
          </InputLabel>

          <Select
            value={selectedDesignation}
            label="Designation"
            onChange={(e) =>
              setSelectedDesignation(
                e.target.value
              )
            }
            sx={{
              color:
                theme.palette.text
                  .primary,

              backgroundColor:
                theme.palette.mode ===
                "dark"
                  ? "rgba(255, 255, 255, 0.15)"
                  : "#fff",

              borderRadius:
                "10px",

              "& fieldset": {
                borderRadius:
                  "10px",
              },
            }}
          >
            <MenuItem value="">
              All Designations
            </MenuItem>

            {filteredDesignations.map(
              (desig) => (
                <MenuItem
                  key={desig.id}
                  value={desig.id}
                >
                  {desig.name}
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>

        {/* ADD ATTENDANCE */}

      <Button
  variant="contained"
  sx={{
    width: "185px",
    borderRadius: "10px",
    height: "50px",
    fontWeight: "bold",
  }}
  startIcon={<AddIcon />}
  onClick={() =>
    navigate("/add-employee-attendance", {
      state: {
        branchId: selectedBranchId,
        organizationId: organizationId,
      },
    })
  }
>
  ADD ATTENDANCE
</Button>

        <Box
          sx={{
            flexGrow: 1,
          }}
        />

        {/* INFO */}

        <IconButton
          onClick={() =>
            setShowUserGuideModal(
              true
            )
          }
          sx={{
            width: 60,
            height: 60,
            borderRadius: "50%",
          }}
        >
          <InfoOutlineIcon
            sx={{
              fontSize: 30,
            }}
          />
        </IconButton>
      </Box>

      {/* =====================================================
          EMPLOYEE TABLE
      ===================================================== */}

      <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: "100%",
          boxSizing: "border-box",
          mx: "auto",
          backgroundColor:
            theme.palette.background
              .paper,
          borderRadius: "16px",
        }}
      >
        <TableContainer
          sx={{
            width: "100%",
            borderRadius: "16px",
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                {[
                  "Serial No",
                  "Employee Code",
                  "Employee Name",
                  "Department",
                  "Designation",
                  "Actions",
                ].map((head) => (
                  <TableCell
                    key={head}
                    sx={{
                      fontWeight:
                        "bold",
                      backgroundColor:
                        theme.palette
                          .primary
                          .main,
                      color: "white",
                      fontSize:
                        "1.1rem",
                    }}
                    align={
                      head ===
                      "Actions"
                        ? "center"
                        : "left"
                    }
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                  >
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedData.length >
                0 ? (
                paginatedData.map(
                  (
                    emp,
                    index
                  ) => (
                    <TableRow
                      key={emp.id}
                      hover
                      sx={{
                        "&:hover": {
                          backgroundColor:
                            "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <TableCell>
                        {(page - 1) *
                          rowsPerPage +
                          index +
                          1}
                      </TableCell>

                      <TableCell>
                        {emp.code ||
                          emp.employee_code ||
                          "N/A"}
                      </TableCell>

                      <TableCell>
                        {emp.name ||
                          emp.employee_name ||
                          "N/A"}
                      </TableCell>

                      <TableCell>
                        {deptMap[
                          emp.department_id ??
                            emp.dept_id ??
                            emp.department ??
                            emp.dept ??
                            ""
                        ] ||
                          "N/A"}
                      </TableCell>

                      <TableCell>
                        {desigMap[
                          emp.designation_id ??
                            emp.desig_id ??
                            emp.designation ??
                            emp.desig ??
                            ""
                        ] ||
                          "N/A"}
                      </TableCell>

                      <TableCell align="center">
                        <Tooltip
                          title="View Attendance"
                          arrow
                        >
                          <IconButton
                            sx={{
                              color:
                                theme
                                  .palette
                                  .primary
                                  .light,
                            }}
                            onClick={() => {
                              const empId =
                                emp.employee ||
                                emp.employee_id ||
                                emp.id ||
                                emp.pk;

                              if (
                                !empId
                              ) {
                                setSnackbarMessage(
                                  "Cannot view attendance – missing employee identifier"
                                );

                                setSnackbarSeverity(
                                  "warning"
                                );

                                setOpenSnackbar(
                                  true
                                );

                                return;
                              }

                              setSelectedEmployee(
                                emp
                              );

                              fetchEmployeeAttendance(
                                empId
                              );

                              setOpenAttendanceModal(
                                true
                              );
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )
                )
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                  >
                    No Data Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* =====================================================
          PAGINATION
      ===================================================== */}

      {filteredData.length >
        0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "center",
            mt: 1,
            p: 2,
          }}
        >
          <Typography
            variant="body2"
            color={
              theme.palette
                .primary.main
            }
          >
            Page{" "}
            <strong>
              {page}
            </strong>{" "}
            of{" "}
            <strong>
              {totalPages}
            </strong>
          </Typography>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
          >
            <IconButton
              onClick={() =>
                handleChangePage(
                  page - 1
                )
              }
              disabled={
                page === 1
              }
            >
              <ChevronLeft />
            </IconButton>

            {[
              ...Array(
                totalPages
              ),
            ].map(
              (_, index) => (
                <IconButton
                  key={
                    index + 1
                  }
                  onClick={() =>
                    handleChangePage(
                      index + 1
                    )
                  }
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius:
                      "50%",
                    backgroundColor:
                      page ===
                      index + 1
                        ? theme
                            .palette
                            .primary
                            .main
                        : "transparent",
                    color:
                      page ===
                      index + 1
                        ? theme
                            .palette
                            .primary
                            .contrastText
                        : theme
                            .palette
                            .text
                            .primary,
                  }}
                >
                  {index + 1}
                </IconButton>
              )
            )}

            <IconButton
              onClick={() =>
                handleChangePage(
                  page + 1
                )
              }
              disabled={
                page >=
                totalPages
              }
            >
              <ChevronRight />
            </IconButton>

            <FormControl
              size="small"
              sx={{
                minWidth: 100,
                ml: 1,
              }}
            >
              <InputLabel>
                Rows
              </InputLabel>

              <Select
                value={
                  rowsPerPage
                }
                onChange={
                  handleChangeRowsPerPage
                }
                label="Rows"
              >
                {rowsPerPageOptions.map(
                  (opt) => (
                    <MenuItem
                      key={opt}
                      value={opt}
                    >
                      {opt}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      {/* =====================================================
          ATTENDANCE MODAL
      ===================================================== */}

      <Dialog
        open={
          openAttendanceModal
        }
        onClose={() =>
          setOpenAttendanceModal(
            false
          )
        }
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius:
              "16px",
            overflow:
              "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color:
              theme.palette
                .primary
                .contrastText,
            fontWeight:
              "bold",
            fontSize:
              "1.3rem",
          }}
        >
          Monthly Attendance –{" "}
          {selectedEmployee?.name ||
            selectedEmployee?.employee_name ||
            "Employee"}
        </DialogTitle>

        <DialogContent
          sx={{ p: 0 }}
        >
          <CustomTableScrollbar
            style={{
              maxHeight: 520,
              overflowY:
                "auto",
            }}
          >
            {/* SUMMARY */}

            <Box
              sx={{
                display:
                  "flex",
                flexWrap:
                  "wrap",
                alignItems:
                  "center",
                gap: 3,
                px: 2,
                py: 2,
                mb: 2,
                borderBottom:
                  `1px solid ${theme.palette.divider}`,
                fontSize:
                  "15px",
                fontWeight:
                  600,
              }}
            >
              <Typography>
                <strong>
                  Present:
                </strong>{" "}
                <span
                  style={{
                    color:
                      "#2e7d32",
                  }}
                >
                  {
                    attendanceSummary?.present_days ??
                    0
                  }
                </span>
              </Typography>

              <Typography>
                <strong>
                  Absent:
                </strong>{" "}
                <span
                  style={{
                    color:
                      "#d32f2f",
                  }}
                >
                  {
                    attendanceSummary?.absent_days ??
                    0
                  }
                </span>
              </Typography>

              <Typography>
                <strong>
                  Half Day:
                </strong>{" "}
                <span
                  style={{
                    color:
                      "#ed6c02",
                  }}
                >
                  {
                    attendanceSummary?.half_days ??
                    0
                  }
                </span>
              </Typography>

              <Typography>
                <strong>
                  Leave:
                </strong>{" "}
                <span
                  style={{
                    color:
                      "#0288d1",
                  }}
                >
                  {
                    attendanceSummary?.leave_days ??
                    0
                  }
                </span>
              </Typography>

              <Typography>
                <strong>
                  Working Days:
                </strong>{" "}
                <span
                  style={{
                    color:
                      "#7b1fa2",
                  }}
                >
                  {
                    attendanceSummary?.working_days ??
                    0
                  }
                </span>
              </Typography>

              <Typography>
                <strong>
                  Attendance:
                </strong>{" "}
                <span
                  style={{
                    color:
                      theme.palette
                        .primary
                        .main,
                  }}
                >
                  {
                    attendanceSummary?.attendance_percentage ??
                    0
                  }
                  %
                </span>
              </Typography>
            </Box>

            {/* MONTHLY TABLE */}

            <TableContainer>
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth:
                    1200,

                  "& th, & td":
                    {
                      border:
                        "1px solid",
                      borderColor:
                        theme.palette
                          .divider,
                      whiteSpace:
                        "nowrap",
                      px: 1.5,
                      py: 1,
                    },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      align="center"
                      sx={{
                        minWidth:
                          140,
                        fontWeight:
                          "bold",
                        position:
                          "sticky",
                        left: 0,
                        zIndex: 3,
                        backgroundColor:
                          theme
                            .palette
                            .background
                            .paper,
                      }}
                    >
                      Month
                    </TableCell>

                    {[
                      ...Array(
                        31
                      ),
                    ].map(
                      (_, i) => (
                        <TableCell
                          key={i}
                          align="center"
                          sx={{
                            minWidth:
                              42,
                            fontWeight:
                              "bold",
                            backgroundColor:
                              theme
                                .palette
                                .background
                                .paper,
                          }}
                        >
                          {i + 1}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {monthOrder.map(
                    (month) => {
                      const days =
                        employeeAttendanceData[
                          month
                        ] ||
                        new Array(
                          31
                        ).fill(
                          null
                        );

                      return (
                        <TableRow
                          key={
                            month
                          }
                          hover
                        >
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight:
                                "bold",
                              position:
                                "sticky",
                              left: 0,
                              backgroundColor:
                                theme
                                  .palette
                                  .background
                                  .paper,
                              zIndex: 2,
                            }}
                          >
                            {
                              month
                            }
                          </TableCell>

                          {days
                            .slice(
                              0,
                              getDaysInMonth(
                                month
                              )
                            )
                            .map(
                              (
                                cell,
                                index
                              ) => {
                                const status =
                                  typeof cell ===
                                  "string"
                                    ? cell
                                    : cell?.status ||
                                      "-";

                                const record =
                                  typeof cell ===
                                  "object"
                                    ? cell?.record
                                    : null;

                                const timeIn =
                                  record?.time_in ||
                                  null;

                                const timeOut =
                                  record?.time_out ||
                                  null;

                                const hasTimeDetails =
                                  Boolean(
                                    timeIn ||
                                      timeOut
                                  );

                                const statusContent =
                                  status ===
                                  "P" ? (
                                    <Chip
                                      label="P"
                                      color="success"
                                      size="small"
                                    />
                                  ) : status ===
                                    "A" ? (
                                    <Chip
                                      label="A"
                                      color="error"
                                      size="small"
                                    />
                                  ) : status ===
                                    "H" ? (
                                    <Chip
                                      label="H"
                                      color="warning"
                                      size="small"
                                    />
                                  ) : status ===
                                    "L" ? (
                                    <Chip
                                      label="L"
                                      color="info"
                                      size="small"
                                    />
                                  ) : (
                                    status
                                  );

                                const cellContent =
                                  hasTimeDetails ? (
                                    <Tooltip
                                      title={
                                        <Box
                                          sx={{
                                            p: 0.5,
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: 700,
                                            }}
                                          >
                                            Time In:{" "}
                                            {timeIn ||
                                              "N/A"}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: 700,
                                            }}
                                          >
                                            Time Out:{" "}
                                            {timeOut ||
                                              "N/A"}
                                          </Typography>
                                        </Box>
                                      }
                                      arrow
                                      placement="top"
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          display:
                                            "inline-flex",
                                          alignItems:
                                            "center",
                                          cursor:
                                            "pointer",
                                        }}
                                      >
                                        {
                                          statusContent
                                        }
                                      </Box>
                                    </Tooltip>
                                  ) : (
                                    statusContent
                                  );

                                return (
                                  <TableCell
                                    key={index}
                                    align="center"
                                    sx={{
                                      fontWeight:
                                        status ===
                                          "P" ||
                                        status ===
                                          "A"
                                          ? 900
                                          : 500,

                                      color:
                                        status ===
                                        "P"
                                          ? "#2e7d32"
                                          : status ===
                                            "A"
                                            ? "#c62828"
                                            : status ===
                                              "Sunday"
                                              ? theme
                                                  .palette
                                                  .text
                                                  .disabled
                                              : "#686868",
                                    }}
                                  >
                                    {cellContent}
                                  </TableCell>
                                );
                              }
                            )}
                        </TableRow>
                      );
                    }
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CustomTableScrollbar>

          {/* =================================================
              ATTENDANCE DETAILS
          ================================================= */}

          <Box
            sx={{
              mt: 4,
              px: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight:
                  700,
                mb: 2,
                color:
                  theme.palette
                    .primary
                    .main,
              }}
            >
              Attendance Details
            </Typography>

            <Box
              sx={{
                display:
                  "flex",
                gap: 2,
                mb: 2,
                mt: 2,
                alignItems:
                  "center",
                flexWrap:
                  "wrap",
              }}
            >
              <TextField
                label="From Date"
                type="date"
                value={
                  fromDate
                }
                onChange={(
                  e
                ) =>
                  setFromDate(
                    e.target
                      .value
                  )
                }
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
              />

              <TextField
                label="To Date"
                type="date"
                value={toDate}
                onChange={(
                  e
                ) =>
                  setToDate(
                    e.target
                      .value
                  )
                }
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
              />

              <Button
                variant="contained"
                onClick={
                  applyDateFilter
                }
              >
                Apply
              </Button>

              <Button
                variant="outlined"
                onClick={
                  resetDateFilter
                }
              >
                Reset
              </Button>
            </Box>

            {/* DETAILS TABLE */}

            <TableContainer
              component={Paper}
            >
              <Table
                size="small"
              >
                <TableHead>
                  <TableRow>
                    <TableCell>
                      S.No
                    </TableCell>

                    <TableCell>
                      Date
                    </TableCell>

                    <TableCell>
                      Day
                    </TableCell>

                    <TableCell>
                      Status
                    </TableCell>

                    <TableCell>
                      Time In
                    </TableCell>

                    <TableCell>
                      Time Out
                    </TableCell>

                    <TableCell>
                      Total Hours
                    </TableCell>

                    <TableCell>
                      Remark
                    </TableCell>

                    <TableCell align="center">
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredAttendanceHistory.length ===
                  0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        align="center"
                      >
                        No attendance found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAttendanceHistory.map(
                      (
                        row,
                        index
                      ) => (
                        <TableRow
                          key={
                            row.id
                          }
                        >
                          <TableCell>
                            {index +
                              1}
                          </TableCell>

                          <TableCell>
                            {
                              row.date
                            }
                          </TableCell>

                          <TableCell>
                            {
                              row.day
                            }
                          </TableCell>

                          <TableCell>
                            {row.status ===
                              "present" && (
                              <Chip
                                label="Present"
                                color="success"
                                size="small"
                              />
                            )}

                            {row.status ===
                              "absent" && (
                              <Chip
                                label="Absent"
                                color="error"
                                size="small"
                              />
                            )}

                            {row.status ===
                              "half_day" && (
                              <Chip
                                label="Half Day"
                                color="warning"
                                size="small"
                              />
                            )}

                            {row.status ===
                              "on_leave" && (
                              <Chip
                                label="Leave"
                                color="info"
                                size="small"
                              />
                            )}
                          </TableCell>

                          <TableCell>
                            {row.time_in ? (
                              <Typography
                                fontWeight={
                                  600
                                }
                                color="success.main"
                              >
                                {
                                  row.time_in
                                }
                              </Typography>
                            ) : (
                              <Typography color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            {row.time_out ? (
                              <Typography
                                fontWeight={
                                  600
                                }
                                color="error.main"
                              >
                                {
                                  row.time_out
                                }
                              </Typography>
                            ) : (
                              <Typography color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            {row.total_hours ? (
                              <Typography
                                fontWeight={
                                  700
                                }
                                color="primary"
                              >
                                {
                                  row.total_hours
                                }
                              </Typography>
                            ) : (
                              <Typography color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            {row.remark ? (
                              row.remark
                            ) : (
                              <Typography
                                color="text.secondary"
                                fontStyle="italic"
                              >
                                No Remark
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell align="center">
                            <Tooltip
                              title="Edit Attendance"
                            >
                              <IconButton
                                color="primary"
                                onClick={() =>
                                  handleEditAttendance(
                                    row
                                  )
                                }
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>

                            <Tooltip
                              title="Delete Attendance"
                            >
                              <IconButton
                                color="error"
                                onClick={() =>
                                  handleDeleteClick(
                                    row
                                  )
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      )
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
          }}
        >
          <Button
            variant="contained"
            onClick={() =>
              setOpenAttendanceModal(
                false
              )
            }
            sx={{
              minWidth: 120,
            }}
          >
            CLOSE
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          EDIT DIALOG
      ===================================================== */}

      <Dialog
        open={openEditDialog}
        onClose={() =>
          setOpenEditDialog(false)
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Attendance
        </DialogTitle>

        <DialogContent
          sx={{
            mt: 2,
          }}
        >
          <FormControl
            fullWidth
            sx={{
              mb: 2,
            }}
          >
            <InputLabel>
              Status
            </InputLabel>

            <Select
              value={editStatus}
              label="Status"
              onChange={(e) =>
                setEditStatus(
                  e.target.value
                )
              }
            >
              <MenuItem value="present">
                Present
              </MenuItem>

              <MenuItem value="absent">
                Absent
              </MenuItem>

              <MenuItem value="half_day">
                Half Day
              </MenuItem>

              <MenuItem value="on_leave">
                Leave
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            type="time"
            label="Time In"
            value={editTimeIn}
            onChange={(e) =>
              setEditTimeIn(
                e.target.value
              )
            }
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              mb: 2,
            }}
          />

          <TextField
            fullWidth
            type="time"
            label="Time Out"
            value={editTimeOut}
            onChange={(e) =>
              setEditTimeOut(
                e.target.value
              )
            }
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              mb: 2,
            }}
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Remark"
            value={editRemark}
            onChange={(e) =>
              setEditRemark(
                e.target.value
              )
            }
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setOpenEditDialog(
                false
              )
            }
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={
              updateLoading
            }
            onClick={
              handleUpdateAttendance
            }
          >
            {updateLoading
              ? "Updating..."
              : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          DELETE DIALOG
      ===================================================== */}

      <Dialog
        open={openDeleteDialog}
        onClose={() =>
          setOpenDeleteDialog(
            false
          )
        }
      >
        <DialogTitle>
          Delete Attendance
        </DialogTitle>

        <DialogContent>
          Are you sure you want to
          delete this attendance
          record?
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setOpenDeleteDialog(
                false
              );

              setDeleteAttendanceId(
                null
              );
            }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            disabled={
              deleteLoading
            }
            onClick={
              handleDeleteAttendance
            }
          >
            {deleteLoading
              ? "Deleting..."
              : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          USER GUIDE
      ===================================================== */}

      <Dialog
        open={
          showUserGuideModal
        }
        onClose={() =>
          setShowUserGuideModal(
            false
          )
        }
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius:
              "16px",
            overflow:
              "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: "#fff",
            fontWeight:
              "bold",
            fontSize:
              "2rem",
            position:
              "relative",
          }}
        >
          Employee Attendance -
          User Guide

          <IconButton
            onClick={() =>
              setShowUserGuideModal(
                false
              )
            }
            sx={{
              color: "#fff",
              position:
                "absolute",
              right: 14,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            maxHeight:
              "70vh",
            overflowY:
              "auto",
            padding: "20px",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight:
                "bold",
              color:
                theme.palette
                  .primary.main,
              mb: 1,
            }}
          >
            ➤ Follow the steps
            below to view employee
            attendance:
          </Typography>

          <ul
            style={{
              marginLeft:
                "20px",
              lineHeight:
                "1.8",
            }}
          >
            <li>
              Click the{" "}
              <strong>
                View Attendance
              </strong>{" "}
              eye icon in the
              Actions column.
            </li>

            <li>
              A modal will
              display the
              employee's monthly
              attendance.
            </li>

            <li>
              Scroll horizontally
              to view attendance
              for each day.
            </li>
          </ul>

          <Typography
            variant="body1"
            sx={{
              fontWeight:
                "bold",
              color:
                theme.palette
                  .primary.main,
              mt: 3,
              mb: 1,
            }}
          >
            ➤ Search Employee
          </Typography>

          <ul
            style={{
              marginLeft:
                "20px",
              lineHeight:
                "1.8",
            }}
          >
            <li>
              Use the search bar
              at the top.
            </li>

            <li>
              Search by employee
              name or code.
            </li>

            <li>
              Results update
              automatically.
            </li>
          </ul>

          <Typography
            variant="body1"
            sx={{
              fontWeight:
                "bold",
              color:
                theme.palette
                  .primary.main,
              mt: 3,
              mb: 1,
            }}
          >
            ➤ Add Attendance
          </Typography>

          <ul
            style={{
              marginLeft:
                "20px",
              lineHeight:
                "1.8",
            }}
          >
            <li>
              Click{" "}
              <strong>
                ADD ATTENDANCE
              </strong>
              .
            </li>

            <li>
              Fill the required
              attendance fields.
            </li>

            <li>
              Click{" "}
              <strong>
                MARK ATTENDANCE
              </strong>
              .
            </li>
          </ul>

          <Typography
            variant="body1"
            sx={{
              fontWeight:
                "bold",
              color:
                theme.palette
                  .primary.main,
              mt: 3,
              mb: 1,
            }}
          >
            ➤ Pagination
          </Typography>

          <ul
            style={{
              marginLeft:
                "20px",
              lineHeight:
                "1.8",
            }}
          >
            <li>
              Use pagination at
              the bottom.
            </li>

            <li>
              Use left/right
              arrows.
            </li>

            <li>
              Click page numbers.
            </li>

            <li>
              Change rows per
              page using the
              dropdown.
            </li>
          </ul>
        </DialogContent>
      </Dialog>

      {/* =====================================================
          SNACKBAR
      ===================================================== */}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() =>
          setOpenSnackbar(
            false
          )
        }
      >
        <Alert
          severity={
            snackbarSeverity
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EmployeeAttendance;